// ts-rest contract for the manager-alias directory Ops UI (issue #36).
//
// The directory is firm-global (NOT session-scoped) — it backs cross-cutting
// manager resolution across COAC routing, KPI rollups, overdrawn-cash, and
// reconciliation-break assignment (parent PRD #15). This contract serves the
// Ops-facing CRUD surface that lists / adds / removes alias rows.
//
// Writes dispatch through the ontology registry (`CreateManagerAlias` /
// `RemoveManagerAlias`); reads join the global `manager_aliases` table with
// the `users` directory so the UI can show the target user's name + email
// inline. Persona-gated to ops/admin (matching the Actions themselves).
//
// Endpoints under /api/manager-aliases:
//   GET    /api/manager-aliases      — list every alias with its target user
//   POST   /api/manager-aliases      — create an alias bound to a user
//   DELETE /api/manager-aliases/:id  — remove an alias by id

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

// ---------------------------------------------------------------------------
// Data shapes
// ---------------------------------------------------------------------------

export const managerAliasSourceSchema = z.enum(['accounts_json', 'ops_added']);
export type ManagerAliasSource = z.infer<typeof managerAliasSourceSchema>;

/** A ManagerAlias row with its target User joined inline. */
export const managerAliasRowSchema = z.object({
    id: z.string(),
    alias: z.string(),
    normalizedAlias: z.string(),
    userId: z.string(),
    source: managerAliasSourceSchema,
    createdAt: z.string(),
    createdBy: z.string().nullable(),
    // Joined target user — null when the bound userId has no matching row
    // (orphaned alias). The UI surfaces this as a recoverable error rather
    // than hiding the alias.
    user: z
        .object({
            id: z.string(),
            name: z.string().nullable(),
            email: z.string(),
        })
        .nullable(),
});
export type ManagerAliasRow = z.infer<typeof managerAliasRowSchema>;

export const listManagerAliasesResponseSchema = z.object({
    aliases: z.array(managerAliasRowSchema),
});
export type ListManagerAliasesResponse = z.infer<typeof listManagerAliasesResponseSchema>;

export const createManagerAliasRequestSchema = z.object({
    alias: z.string().min(1),
    userId: z.string().min(1),
});
export type CreateManagerAliasRequest = z.infer<typeof createManagerAliasRequestSchema>;

// ---------------------------------------------------------------------------
// Re-attribution summary (C6). Every alias write triggers a canonical
// re-attribution backfill (dispatching UpdateClient) so a first correct alias
// re-stamps the ex-book, and removing a stale alias CLEARS the no-longer-
// resolving attributions to null. The backfill runs in a catch — its failure
// NEVER converts a committed alias write into an HTTP error; it surfaces here.
// ---------------------------------------------------------------------------

export const reattributionUnresolvedSchema = z.object({
    name: z.string(),
    portfolioCount: z.number(),
    reason: z.enum(['no_match', 'ambiguous']),
});
export type ReattributionUnresolved = z.infer<typeof reattributionUnresolvedSchema>;

export const reattributionSummarySchema = z.object({
    /** Canonical clients examined against the shared grid-string rule. */
    examined: z.number(),
    /** Clients re-stamped to a newly-resolved IM. */
    updated: z.number(),
    /** Clients whose stale attribution was cleared to null (no_match). */
    cleared: z.number(),
    /** Manager strings that stayed unresolved (report-only for `ambiguous`). */
    unresolved: z.array(reattributionUnresolvedSchema),
    /** Present iff the backfill threw AFTER the alias write committed. */
    error: z.string().optional(),
});
export type ReattributionSummary = z.infer<typeof reattributionSummarySchema>;

export const createManagerAliasResponseSchema = z.object({
    ok: z.literal(true),
    alias: managerAliasRowSchema,
    reattribution: reattributionSummarySchema,
});
export type CreateManagerAliasResponse = z.infer<typeof createManagerAliasResponseSchema>;

import { errorBodyWithCode as errorBody } from '../schemas/common';

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const managerAliasContract = c.router(
    {
        list: {
            method: 'GET',
            path: '/manager-aliases',
            responses: {
                200: listManagerAliasesResponseSchema,
            },
            summary: 'List every ManagerAlias row with its target User joined',
        },
        create: {
            method: 'POST',
            path: '/manager-aliases',
            body: createManagerAliasRequestSchema,
            responses: {
                200: createManagerAliasResponseSchema,
                // alias_conflict surfaces here as 409 with code='alias_conflict'.
                400: errorBody,
                409: errorBody,
            },
            summary: 'Create a manager alias bound to a user (ops_added source)',
        },
        remove: {
            method: 'DELETE',
            path: '/manager-aliases/:id',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({
                    ok: z.literal(true),
                    reattribution: reattributionSummarySchema,
                }),
                404: errorBody,
            },
            summary: 'Remove a manager alias by id (re-attributes affected clients)',
        },
        reattribute: {
            method: 'POST',
            path: '/manager-aliases/reattribute',
            body: z.object({}).optional(),
            responses: {
                200: z.object({
                    ok: z.literal(true),
                    reattribution: reattributionSummarySchema,
                }),
                400: errorBody,
            },
            summary: 'Manually re-run the IM re-attribution backfill (retry path)',
        },
    },
    {
        pathPrefix: '/api',
        strictStatusCodes: false,
    },
);
